import { LitElement, html, css } from 'lit';
import { property, customElement } from 'lit/decorators.js';

import {
  PickupOrderStatusReadyToBeProcessed,
  PickupOrderStatusInitState,
  PickupOrderStatusReadyForPickUp,
  PickupOrderStatusStartLoadingInProgress,
  PickupOrderStatusInitStateInProgress,
  PickupOrderStatusStartLoading,
  formatDate,
  formatTime,
  formatTimeWithoutDate,
  PickupOrderStatusBeingRemoved,
  PickupOrderStatusToBeRemoved,
} from '../../util/util';
import '../../elements/langhout-button/langhout-button';
import '../../elements/langhout-checkbox/langhout-checkbox';
import '../../elements/add-comment/add-comment';
import { AddComment } from '../../elements/add-comment/add-comment';
import { authenticatedAPICall } from '../../client/api-client';

import {
  Comment,
  PickupOrder,
  PickupSubOrder,
  Product,
  getOrderIDs,
  getShippingIDs,
  getShippingCities,
  getCustomerNames,
} from '../../types/types';

@customElement('pickup-order')
export class PickupOrderElement extends LitElement {
  static styles = css`
    .card {
      border: 1px solid var(--text-color-main);
      padding: 12px;
      margin-bottom: 20px;
      border-radius: 10px;
      position: relative;
    }

    .row {
      width: 100%;
      display: inline-flex;
      flex-direction: row;
      justify-content: left;
    }

    .wrap-row {
      flex-wrap: wrap;
      flex-flow: row wrap;
    }

    .prop-wrapper {
      display: flex;
      flex-direction: row;
      flex-wrap: nowrap;
      flex-flow: nowrap;
    }

    .prod-row {
      margin-top: 10px;
      text-align: left;
    }

    h3 {
      margin: 0;
    }

    .fill {
      flex-grow: 1;
    }

    .bottom-text {
      margin-top: 10px;
    }

    .label {
      color: var(--primary-color);
      min-width: 130px;
      text-align: left;
    }

    .normal-text {
      text-align: left;
      max-width: 120px;
      padding-right: 15px;
      overflow-wrap: break-word;
    }

    @media (min-width: 475px) {
      .normal-text {
        min-width: 120px;
      }
    }

    langhout-button {
      margin-top: 20px;
    }

    .button-container {
      max-width: 200px;
    }

    .order-icon {
      position: absolute;
      top: 10px;
      right: 10px;
    }

    svg {
      width: 30px;
      height: 30px;
    }

    .divider {
      margin-top: 10px;
      margin-bottom: 5px;
      border-top: 1px solid black;
    }

    .divider-small {
      margin-left: 10px;
      margin-right: 10px;
      margin-top: 10px;
      margin-bottom: 5px;
      border-top: 1px solid black;
    }

    .icon-space {
      width: 70px;
    }

    .prod-amount-text {
      color: var(--primary-color);
      font-weight: bold;
      margin-right: 20px;
    }

    .comment-wrapper {
      margin-top: 10px;
      padding: 10px;
      border: 1px solid var(--text-color-main);
      border-radius: 10px;
    }

    .comment-user-name,
    .comment-time {
      color: var(--primary-color);
      font-weight: bold;
    }

    @media (min-width: 701px) {
      .prod-amount-text {
        display: inline-block;
        min-width: 180px;
      }
    }

    add-comment {
      z-index: 9999;
      display: none;
      position: absolute;
      top: 0;
      left: 0;
    }

    :host([displayAddComment]) add-comment {
      display: flex;
    }

    .warning {
      margin-top: 15px;
      color: red;
      font-weight: 900;
    }

    .book-directly-text {
      margin-left: 40px;
      text-align: left;
    }

    .button-row {
      margin-top: 10px;
      flex-wrap: wrap;
    }

    .half-width {
      width: 50%;
      min-width: 150px;
    }
  `;

  @property({ type: Object, attribute: true, reflect: true })
  public pickupOrder: PickupOrder = <PickupOrder>{};

  @property({ type: Boolean, attribute: true, reflect: true })
  public displayOnly = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  public cleanup = false;

  @property({ type: Boolean, attribute: true, reflect: true })
  public displayCommentButton = false;

  @property({ type: Boolean, reflect: true })
  private displayAddComment = false;

  constructor() {
    super();
  }

  render() {
    if (!this.pickupOrder.id) {
      return html``;
    }
    return html`
      <add-comment
        id="add-comment-${this.pickupOrder.id}"
        url="/pickuporders/${this.pickupOrder.id}/comment"
        @close="${() => {
          this.displayAddComment = false;
        }}"
      ></add-comment>
      <div class="card">
        ${this.renderIcon(this.pickupOrder)}
        <div class="row wrap-row">
          <div class="prop-wrapper">
            <div class="label">Order:</div>
            <div class="normal-text">${getOrderIDs(this.pickupOrder)}</div>
          </div>
          <div class="prop-wrapper">
            <div class="label">Laaddatum:</div>
            <div class="normal-text">
              ${formatDate(this.pickupOrder.pickup_date)}
            </div>
            <div class="icon-space"></div>
          </div>
        </div>
        ${this.renderTaskTimes()}
        <div class="row wrap-row">
          <div class="prop-wrapper">
            <div class="label">Klant:</div>
            <div class="normal-text">${getCustomerNames(this.pickupOrder)}</div>
          </div>
          <div class="prop-wrapper">
            <div class="label">Plaatsnaam:</div>
            <div class="normal-text">
              ${getShippingCities(this.pickupOrder)}
            </div>
          </div>
        </div>
        <div class="row wrap-row">
          <div class="prop-wrapper">
            <div class="label">Vracht ID:</div>
            <div class="normal-text">${getShippingIDs(this.pickupOrder)}</div>
          </div>
          <div class="prop-wrapper">
            <div class="label">Gewicht:</div>
            <div class="normal-text">
              ${this.getWeightTotalText(this.pickupOrder)}
            </div>
          </div>
        </div>
        <div class="row wrap-row">
          <div class="prop-wrapper">
            ${this.renderLicensePlate(this.pickupOrder)}
          </div>
          <div class="prop-wrapper">
            ${this.renderTransporter(this.pickupOrder)}
          </div>
        </div>
        <div class="divider"></div>
        ${this.pickupOrder.sub_orders.map((subOrder, index) => {
          return html`
            ${this.renderDivider(index)}
            ${subOrder.products.map(product => {
              if (this.displayOnly || this.cleanup) {
                return html` <div class="row prod-row">
                  <span class="prod-amount-text">
                    ${product.amount_packs}
                    ${this.getAmountText(
                      product.amount_packs,
                      product.product_type,
                    )}
                    ${this.getPiecesText(product.amount, product.product_type)}
                  </span>
                  <span> ${product.description} </span>
                </div>`;
              }
              return html`
                <div class="row prod-row">
                  <langhout-checkbox
                    .checked=${this.isChecked(this.pickupOrder, product)}
                    @click=${() => {
                      this.setProductDone(this.pickupOrder, subOrder, product);
                    }}
                  ></langhout-checkbox>
                  <div>
                    <span class="prod-amount-text">
                      ${product.amount_packs}
                      ${this.getAmountText(
                        product.amount_packs,
                        product.product_type,
                      )}
                      ${this.getPiecesText(
                        product.amount,
                        product.product_type,
                      )}
                    </span>
                    <span> ${product.description} </span>
                  </div>
                </div>
              `;
            })}
          `;
        })}
        ${this.renderComments(this.pickupOrder)}
        ${this.renderNotReadyForPickup(this.pickupOrder)}
        ${this.renderHasReturnProducts(this.pickupOrder)}
        <div class="row button-row">
          <div class="half-width row button-row">
            <div class="button-container">${this.renderAddCommentButton()}</div>
            <div class="fill"></div>
          </div>
          <div class="half-width row button-row">
            ${this.renderButton(this.pickupOrder)}
          </div>
        </div>
      </div>
    `;
  }

  setProductDone = async (
    pickupOrder: PickupOrder,
    pickupSubOrder: PickupSubOrder,
    product: Product,
  ) => {
    let taskType = 1;
    if (
      pickupOrder.status === PickupOrderStatusStartLoadingInProgress ||
      pickupOrder.status === PickupOrderStatusStartLoading
    ) {
      taskType = 2;
    }
    let updateValue = false;
    if (taskType === 1) {
      product.ready_for_pickup = !product.ready_for_pickup;
      updateValue = product.ready_for_pickup;
    } else {
      product.has_been_loaded = !product.has_been_loaded;
      updateValue = product.has_been_loaded;
    }
    const path = `/pickupsuborders/${pickupSubOrder.id}/task-complete`;
    const body = {
      product_id: product.id,
      task_type: taskType,
      is_done: updateValue,
    };
    await authenticatedAPICall(
      path,
      'POST',
      new Headers(),
      JSON.stringify(body),
    );
  };

  isChecked = (pickupOrder: PickupOrder, product: Product): boolean => {
    if (
      (pickupOrder.status === PickupOrderStatusInitStateInProgress ||
        pickupOrder.status === PickupOrderStatusReadyToBeProcessed) &&
      product.ready_for_pickup === true
    ) {
      return true;
    }
    if (
      (pickupOrder.status === PickupOrderStatusStartLoadingInProgress ||
        pickupOrder.status === PickupOrderStatusStartLoading) &&
      product.has_been_loaded === true
    ) {
      return true;
    }
    return false;
  };

  isValidTime = (time: string): boolean => {
    if (!time || time === '') return false;
    return new Date(time).getFullYear() >= 2020;
  };

  renderTaskTimes = () => {
    const times: { label: string; value: string }[] = [
      {
        label: 'Klaargezet om:',
        value: this.pickupOrder.ready_for_pickup_time,
      },
      { label: 'Geladen om:', value: this.pickupOrder.being_loaded_time },
      { label: 'Opgeruimd om:', value: this.pickupOrder.cleanup_time },
    ].filter(t => this.isValidTime(t.value));

    if (times.length === 0) return html``;

    return html`
      <div class="row wrap-row">
        ${times.map(
          t => html`
            <div class="prop-wrapper">
              <div class="label">${t.label}</div>
              <div class="normal-text">${formatTimeWithoutDate(t.value)}</div>
            </div>
          `,
        )}
      </div>
    `;
  };

  renderNotReadyForPickup = (pickupOrder: PickupOrder) => {
    if (
      pickupOrder.status == PickupOrderStatusStartLoading ||
      pickupOrder.status == PickupOrderStatusStartLoadingInProgress
    ) {
      if (pickupOrder.history && pickupOrder.history.length >= 2) {
        let counter = 2;
        if (pickupOrder.status == PickupOrderStatusStartLoadingInProgress) {
          counter = 3;
        }
        if (
          pickupOrder.history[pickupOrder.history.length - counter].status !=
          PickupOrderStatusReadyForPickUp
        ) {
          return html`
            <div class="row">
              <div class="warning">
                Let op: Deze order is nog niet (volledig) klaargezet.
              </div>
            </div>
          `;
        }
      }
    }

    return html``;
  };

  renderHasReturnProducts = (pickupOrder: PickupOrder) => {
    if (pickupOrder.has_return_products) {
      return html`
        <div class="row">
          <div class="warning">Let op: Retour producten.</div>
        </div>
      `;
    }

    return html``;
  };

  renderComments = (pickupOrder: PickupOrder) => {
    if (pickupOrder.comments.length === 0) {
      return html``;
    }
    return html`
      ${pickupOrder.comments.map((comment: Comment) => {
        return html`
          <div class="comment-wrapper">
            <div class="row">
              <div class="comment-user-name">${comment.user_name}</div>
              <div class="fill"></div>
              <div class="comment-time">
                ${formatTime(comment.timestamp, false)}
              </div>
            </div>
            <div class="row">${comment.message}</div>
          </div>
        `;
      })}
    `;
  };

  renderDivider = (index: number) => {
    if (index > 0) {
      return html` <div class="divider-small"></div> `;
    }
    return html``;
  };

  renderIcon = (pickupOrder: PickupOrder) => {
    if (this.displayOnly) {
      return html``;
    }
    switch (pickupOrder.status) {
      case PickupOrderStatusReadyToBeProcessed:
      case PickupOrderStatusInitState:
        return html`
          <div class="order-icon">
            ${this.renderPackageIcon('#de9221', pickupOrder)}
          </div>
        `;
      case PickupOrderStatusInitStateInProgress:
        return html`
          <div class="order-icon">
            ${this.renderPackageIcon('#014E2A', pickupOrder)}
          </div>
        `;
      case PickupOrderStatusStartLoading:
        return html`
          <div class="order-icon">
            ${this.renderTruckIcon('#de9221', pickupOrder)}
          </div>
        `;
      case PickupOrderStatusStartLoadingInProgress:
        return html`
          <div class="order-icon">
            ${this.renderTruckIcon('#014E2A', pickupOrder)}
          </div>
        `;
      case PickupOrderStatusToBeRemoved:
        return html`
          <div class="order-icon">
            ${this.renderBinIcon('#de9221', pickupOrder)}
          </div>
        `;
      case PickupOrderStatusBeingRemoved:
        return html`
          <div class="order-icon">
            ${this.renderBinIcon('#014E2A', pickupOrder)}
          </div>
        `;
      default:
        return html``;
    }
  };

  renderPackageIcon = (color: string, pickupOrder: PickupOrder) => {
    if (pickupOrder.sub_orders.length > 1) {
      return html`${this.packageIcon(color)} ${this.packageIcon(color)}
      ${this.packageIcon(color)}`;
    }
    return html`${this.packageIcon(color)}`;
  };

  packageIcon = (color: string) => {
    return html` <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="${color}"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="feather feather-package"
    >
      <line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line>
      <path
        d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"
      ></path>
      <polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline>
      <line x1="12" y1="22.08" x2="12" y2="12"></line>
    </svg>`;
  };

  renderTruckIcon = (color: string, pickupOrder: PickupOrder) => {
    if (pickupOrder.sub_orders.length > 1) {
      return html`${this.truckIcon(color)} ${this.truckIcon(color)}
      ${this.truckIcon(color)}`;
    }
    return html`${this.truckIcon(color)}`;
  };

  truckIcon = (color: string) => {
    return html` <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="${color}"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="feather feather-truck"
    >
      <rect x="1" y="3" width="15" height="13"></rect>
      <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
      <circle cx="5.5" cy="18.5" r="2.5"></circle>
      <circle cx="18.5" cy="18.5" r="2.5"></circle>
    </svg>`;
  };

  renderBinIcon = (color: string, pickupOrder: PickupOrder) => {
    if (pickupOrder.sub_orders.length > 1) {
      return html`${this.binIcon(color)} ${this.binIcon(color)}
      ${this.binIcon(color)}`;
    }
    return html`${this.binIcon(color)}`;
  };

  binIcon = (color: string) => {
    return html`<svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="${color}"
      stroke-width="2"
      stroke-linecap="round"
      stroke-linejoin="round"
      class="feather feather-trash-2"
    >
      <polyline points="3 6 5 6 21 6"></polyline>
      <path
        d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"
      ></path>
      <line x1="10" y1="11" x2="10" y2="17"></line>
      <line x1="14" y1="11" x2="14" y2="17"></line>
    </svg>`;
  };

  renderTransporter = (pickupOrder: PickupOrder) => {
    if (pickupOrder.transporter === '') {
      return html``;
    }
    return html`
      <div class="row">
        <div class="label">Transporteur:</div>
        <div class="normal-text">${this.pickupOrder.transporter}</div>
      </div>
    `;
  };

  renderLicensePlate = (pickupOrder: PickupOrder) => {
    if (pickupOrder.license_plate === '') {
      return html``;
    }
    return html`
      <div class="row">
        <div class="label">Kenteken:</div>
        <div class="normal-text">${this.pickupOrder.license_plate}</div>
      </div>
    `;
  };

  renderAddCommentButton = () => {
    if (
      this.displayOnly &&
      this.pickupOrder.status !== PickupOrderStatusReadyForPickUp &&
      !this.displayCommentButton
    ) {
      return html``;
    }
    if (this.cleanup) {
      return html``;
    }
    return html`
      <langhout-button
        reverse
        text="Opmerking toevoegen"
        @click="${() => {
          this.displayAddComment = true;
          setTimeout(() => {
            if (this.shadowRoot) {
              const addCommentElement = <AddComment>(
                this.shadowRoot.querySelector(
                  `#add-comment-${this.pickupOrder.id}`,
                )
              );
              addCommentElement.focus();
            }
          }, 10);
        }}"
      ></langhout-button>
    `;
  };

  renderButton = (pickupOrder: PickupOrder) => {
    if (this.displayOnly) {
      return html``;
    }
    let buttonText = 'Oppakken';
    switch (pickupOrder.status) {
      case PickupOrderStatusInitStateInProgress:
        buttonText = 'Klaargezet';
        break;
      case PickupOrderStatusStartLoading:
        buttonText = 'Start laden';
        break;
      case PickupOrderStatusStartLoadingInProgress:
        buttonText = 'Bevestigen laden';
        break;
      case PickupOrderStatusBeingRemoved:
        buttonText = 'Opgeruimd';
        break;
    }

    if (pickupOrder.status === PickupOrderStatusStartLoadingInProgress) {
      return html`
        <div class="button-container">
          <langhout-button
            text="${buttonText}"
            @click="${() => {
              const event = new CustomEvent('button-clicked', {
                detail: {},
              });
              this.dispatchEvent(event);
            }}"
          ></langhout-button>
        </div>
        <div class="fill"></div>
      `;
    }

    return html`
      <div class="fill"></div>
      <div class="button-container">
        <langhout-button
          text="${buttonText}"
          @click="${() => {
            const event = new CustomEvent('button-clicked', {
              detail: {},
            });
            this.dispatchEvent(event);
          }}"
        ></langhout-button>
      </div>
    `;
  };

  getWeightTotalText = (pickupOrder: PickupOrder): string => {
    let totalWeight = 0;
    pickupOrder.sub_orders.forEach((p: PickupSubOrder) => {
      p.products.forEach((p: Product) => {
        totalWeight += p.amount * p.weight;
      });
    });
    totalWeight = Math.round(totalWeight);

    return `${totalWeight.toLocaleString('nl-NL')} kg`;
  };

  getPiecesText = (amount: number, type: number): string => {
    if (type === 2) {
      return '';
    }
    if (amount === 1) {
      return `(${amount} st.)`;
    }
    return `(${amount} sts.)`;
  };

  getAmountText = (amount: number, type: number): string => {
    if (type === 2) {
      if (amount === 1) {
        return 'st.';
      }
      return 'sts.';
    }
    if (amount === 1) {
      return 'pak';
    }
    return 'pakken';
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'pickup-order': PickupOrderElement;
  }
}
